import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import imageCompression from 'browser-image-compression';
import { ThemeContext } from '../../contexts/ThemeContext';
import { authFetch } from '../../services/authFetch';

const KompensasiForm = ({ onClose, initialData = null, readOnly = false, status = null, statusText = null, isEdit = false, onSubmit = null }) => {
  const { darkMode } = useContext(ThemeContext);
  const [formData, setFormData] = useState({
    overtime_date: '',
    start_time: '',
    finish_time: '',
    purpose: '',
    type_or_work: '',
    reflection: '',
    teacher_name: '',
    teacher_id: '',
    images: []
  });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [reflectionCharCount, setReflectionCharCount] = useState(0);
  const [teachers, setTeachers] = useState([]);
  const [teacherSuggestions, setTeacherSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);
  const [imageValidation, setImageValidation] = useState({ 
    hasValidImages: false, 
    errorMessage: '', 
    isPristine: true 
  });

  // Fetch teachers on component mount
  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const response = await authFetch('/api/student/teachers');
        if (response.ok) {
          const result = await response.json();
          // Handle both paginated and direct array responses
          const teachersData = result.data || result;
          setTeachers(Array.isArray(teachersData) ? teachersData : []);
        }
      } catch (error) {
        console.error('Error fetching teachers:', error);
      }
    };
    fetchTeachers();
  }, []);

  // Validate images - ensure at least one image is present
  useEffect(() => {
    const hasValidImages = formData.images && formData.images.length > 0;
    setImageValidation({
      hasValidImages,
      errorMessage: hasValidImages ? '' : 'Minimal 1 foto harus tersedia.',
      isPristine: false
    });
  }, [formData.images]);

  // Load initial data if provided
  useEffect(() => {
    if (initialData) {
      // Make sure images is properly initialized from API response
      const images = initialData.images || [];
      setFormData({
        overtime_date: initialData.overtime_date || '',
        start_time: initialData.start_time || '',
        finish_time: initialData.finish_time || '',
        purpose: initialData.purpose || '',
        type_or_work: initialData.type_or_work || '',
        reflection: initialData.reflection || '',
        teacher_name: initialData.teacher_name || '',
        teacher_id: initialData.teacher_id || '',
        images
      });
      setReflectionCharCount((initialData.reflection || '').length);
      if (initialData.teacher_id) {
        setSelectedTeacherId(initialData.teacher_id);
      }
    }
  }, [initialData]);

  const handleFileChange = async (e) => {
    if (readOnly) return;
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length !== files.length) {
      setNotification({ show: true, message: 'Hanya file gambar yang diperbolehkan.', type: 'error' });
      return;
    }

    try {
      const compressedFiles = await Promise.all(
        imageFiles.map(async (file) => {
          try {
            // Mobile-optimized compression settings
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            const options = {
              maxSizeMB: isMobile ? 0.3 : 0.5, // Smaller size for mobile
              maxWidthOrHeight: isMobile ? 800 : 1024, // Lower resolution for mobile
              useWebWorker: true,
              fileType: 'image/jpeg', // Force JPEG for better compression
              initialQuality: 0.8
            };
            
            let compressedFile;
            if (file.size > (isMobile ? 512 * 1024 : 1024 * 1024)) {
              compressedFile = await imageCompression(file, options);
              
              // Fallback compression if still too large
              if (compressedFile.size > (isMobile ? 300 * 1024 : 500 * 1024)) {
                const fallbackOptions = {
                  ...options,
                  maxSizeMB: isMobile ? 0.2 : 0.3,
                  maxWidthOrHeight: isMobile ? 600 : 800,
                  initialQuality: 0.6
                };
                compressedFile = await imageCompression(compressedFile, fallbackOptions);
              }
            } else {
              compressedFile = file;
            }
            
            // Ensure the result is a proper File object, not a Blob
            if (compressedFile instanceof Blob && !(compressedFile instanceof File)) {
              const fileName = file.name || `compressed_image_${Date.now()}.jpg`;
              compressedFile = new File([compressedFile], fileName, {
                type: compressedFile.type || 'image/jpeg',
                lastModified: Date.now()
              });
            }
            
            return compressedFile;
          } catch (compressionError) {
            console.error('Error during image compression:', compressionError);
            // Return original file if compression fails
            return file;
          }
        })
      );

      const validFiles = compressedFiles.filter(file => file !== null);
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...validFiles].slice(0, 5) // Limit to 5 images
      }));
      
      // Clear the input to allow re-selecting the same file
      e.target.value = '';
      
    } catch (error) {
      console.error('Error processing images:', error);
      setNotification({ 
        show: true, 
        message: 'Gagal memproses gambar. Silakan coba lagi.', 
        type: 'error' 
      });
    }
  };

  const handleChange = (e) => {
    if (readOnly) return;
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      setFormData({
        ...formData,
        images: Array.from(files)
      });
    } else if (name === 'reflection') {
      setFormData({ ...formData, reflection: value });
      setReflectionCharCount(value.length);
    } else if (name === 'teacher_name') {
      setFormData({ ...formData, teacher_name: value, teacher_id: '' });
      setSelectedTeacherId(null);
      
      // Filter teachers based on input
      if (value.trim()) {
        const filtered = teachers.filter(teacher => 
          teacher.name.toLowerCase().includes(value.toLowerCase())
        );
        setTeacherSuggestions(filtered);
        setShowSuggestions(true);
      } else {
        setTeacherSuggestions([]);
        setShowSuggestions(false);
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleTeacherSelect = (teacher) => {
    setFormData({ 
      ...formData, 
      teacher_name: teacher.name,
      teacher_id: teacher.id 
    });
    setSelectedTeacherId(teacher.id);
    setShowSuggestions(false);
    setTeacherSuggestions([]);
  };

  const handleTeacherInputBlur = () => {
    // Delay hiding suggestions to allow for click
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) {
      onClose();
      return;
    }
    
    // If in edit mode and custom onSubmit is provided, use it
    if (isEdit && onSubmit) {
      // Validate images for edit mode
      if (!imageValidation.hasValidImages) {
        setNotification({ show: true, message: imageValidation.errorMessage, type: 'error' });
        return;
      }
      
      setLoading(true);
      try {
        await onSubmit(formData);
      } catch (error) {
        setNotification({ show: true, message: error.message || 'Gagal menyimpan perubahan.', type: 'error' });
      } finally {
        setLoading(false);
      }
      return;
    }
    
    setNotification({ show: false, message: '', type: 'success' });
    setLoading(true);

    
    // Check for cross-midnight scenarios and warn user
    if (formData.start_time && formData.finish_time) {
      const [startHour, startMinute] = formData.start_time.split(':').map(Number);
      const [finishHour, finishMinute] = formData.finish_time.split(':').map(Number);
      const startTotalMinutes = startHour * 60 + startMinute;
      const finishTotalMinutes = finishHour * 60 + finishMinute;
      
      if (finishTotalMinutes <= startTotalMinutes) {
        setNotification({ 
          show: true, 
          message: 'Waktu selesai tidak boleh lebih awal dari waktu mulai. Untuk kegiatan lintas tengah malam, silakan buat laporan terpisah.', 
          type: 'error' 
        });
        setLoading(false);
        return;
      }
    }
    
    const dataToSend = new FormData();
    dataToSend.append('overtime_date', formData.overtime_date);
    dataToSend.append('start_time', formData.start_time);
    dataToSend.append('finish_time', formData.finish_time);
    dataToSend.append('purpose', formData.purpose);
    dataToSend.append('type_or_work', formData.type_or_work);
    dataToSend.append('reflection', formData.reflection);
    dataToSend.append('teacher_name', formData.teacher_name);
    if (formData.teacher_id) {
      dataToSend.append('teacher_id', formData.teacher_id);
    }
    if (formData.images && formData.images.length > 0) {
      formData.images.forEach((file) => {
        // Ensure we're sending File objects, not Blobs
        if (file instanceof Blob && !(file instanceof File)) {
          const fileName = `kompensasi_image_${Date.now()}.jpg`;
          const fileObject = new File([file], fileName, {
            type: file.type || 'image/jpeg',
            lastModified: Date.now()
          });
          dataToSend.append('images[]', fileObject);
        } else if (file instanceof File) {
          dataToSend.append('images[]', file);
        } else {
          console.warn('Invalid file type detected:', typeof file);
        }
      });
    }
    try {
      const response = await authFetch('/api/overtime_reports', {
        method: 'POST',
        body: dataToSend
      });
      const responseText = await response.clone().text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseErr) {
        setNotification({ show: true, message: 'Gagal mengurai respons server.', type: 'error' });
        setLoading(false);
        return;
      }
      if (!response.ok) {
        setNotification({ show: true, message: data.message || 'Gagal mengirim laporan lembur.', type: 'error' });
      } else {
        setNotification({ show: true, message: data.message || 'Laporan lembur berhasil dikirim!', type: 'success' });
        setFormData({
          overtime_date: '',
          start_time: '',
          finish_time: '',
          purpose: '',
          type_or_work: '',
          reflection: '',
          teacher_name: '',
          teacher_id: '',
          images: []
        });
        setReflectionCharCount(0);
        setSelectedTeacherId(null);
        setShowSuggestions(false);
        setTeacherSuggestions([]);
        if (onClose) {
          setTimeout(() => {
            onClose();
          }, 2000);
        }
      }
    } catch (err) {
      setNotification({ show: true, message: 'Terjadi kesalahan jaringan.', type: 'error' });
    }
    setLoading(false);
  };

  return (
    <FormWrapper theme={darkMode ? 'dark' : 'light'}>
      <FormHeader>
        <HeaderContent>
          <h2>{isEdit ? 'Edit Laporan Lembur' : 'Formulir Laporan Lembur'}</h2>
          {status && (
            <StatusBadge status={status}>
              {statusText}
            </StatusBadge>
          )}
        </HeaderContent>
        <CloseButton onClick={onClose}>×</CloseButton>
      </FormHeader>
      
      <FormContent>
        <form onSubmit={handleSubmit}>
          {notification.show && (
            <NotificationToast $type={notification.type}>
              <NotificationMessage>{notification.message}</NotificationMessage>
              <CloseNotificationButton onClick={() => setNotification({ ...notification, show: false })}>
                ×
              </CloseNotificationButton>
            </NotificationToast>
          )}
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="overtime_date">Tanggal Lembur</label>
            <input
              type="date"
              id="overtime_date"
              name="overtime_date"
              value={formData.overtime_date}
              onChange={handleChange}
              required
              readOnly={readOnly}
              disabled={readOnly}
            />
          </FormGroup>
          <FormRow>
            <FormGroup theme={darkMode ? 'dark' : 'light'}>
              <label htmlFor="start_time">Jam Mulai</label>
              <input
                type="time"
                id="start_time"
                name="start_time"
                value={formData.start_time}
                onChange={handleChange}
                required
                readOnly={readOnly}
                disabled={readOnly}
              />
            </FormGroup>
            <FormGroup theme={darkMode ? 'dark' : 'light'}>
              <label htmlFor="finish_time">Jam Selesai</label>
              <input
                type="time"
                id="finish_time"
                name="finish_time"
                value={formData.finish_time}
                onChange={handleChange}
                required
                readOnly={readOnly}
                disabled={readOnly}
              />
            </FormGroup>
          </FormRow>
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="purpose">Alasan Lembur / Tujuan</label>
            <input
              type="text"
              id="purpose"
              name="purpose"
              value={formData.purpose}
              onChange={handleChange}
              placeholder="Contoh: Ada pekerjaan tambahan"
              required
              readOnly={readOnly}
              disabled={readOnly}
            />
          </FormGroup>
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="type_or_work">Jenis Pekerjaan</label>
            <input
              type="text"
              id="type_or_work"
              name="type_or_work"
              value={formData.type_or_work}
              onChange={handleChange}
              placeholder="Contoh: Membersihkan ruang bengkel, memperbaiki alat"
              required
              readOnly={readOnly}
              disabled={readOnly}
            />
          </FormGroup>
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="teacher_name">Nama Guru Bengkel / Pembimbing</label>
            <TeacherSuggestContainer>
              <input
                type="text"
                id="teacher_name"
                name="teacher_name"
                value={formData.teacher_name}
                onChange={handleChange}
                onBlur={handleTeacherInputBlur}
                onFocus={() => {
                  if (formData.teacher_name.trim() && teacherSuggestions.length > 0) {
                    setShowSuggestions(true);
                  }
                }}
                placeholder="Ketik nama guru..."
                required
                readOnly={readOnly}
                disabled={readOnly}
                autoComplete="off"
              />
              {showSuggestions && teacherSuggestions.length > 0 && !readOnly && (
                <SuggestionsDropdown>
                  {teacherSuggestions.map((teacher) => (
                    <SuggestionItem
                      key={teacher.id}
                      onClick={() => handleTeacherSelect(teacher)}
                      selected={selectedTeacherId === teacher.id}
                    >
                      <TeacherName>{teacher.name}</TeacherName>
                    </SuggestionItem>
                  ))}
                </SuggestionsDropdown>
              )}
            </TeacherSuggestContainer>
          </FormGroup>
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="reflection">Refleksi Kegiatan (Opsional)</label>
            <textarea
              id="reflection"
              name="reflection"
              rows="8"
              value={formData.reflection}
              onChange={handleChange}
              placeholder="Tuliskan refleksi kegiatan lembur secara mendalam... (opsional)"
              readOnly={readOnly}
              disabled={readOnly}
            ></textarea>
            <div style={{ fontSize: '0.9em' }}>
              {`Jumlah karakter: ${reflectionCharCount}`}
            </div>
          </FormGroup>
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="images">Upload Foto Dokumentasi (Opsional - Bisa lebih dari 1)</label>
            <input
              type="file"
              id="images"
              name="images"
              multiple
              onChange={handleFileChange}
              accept="image/*"
              disabled={readOnly}
            />
            <ImagePreviewContainer>
              {formData.images && formData.images.map((image, index) => {
                // Handle different image formats
                let imgSrc = '';
                if (image instanceof File) {
                  imgSrc = URL.createObjectURL(image);
                } else if (typeof image === 'string') {
                  imgSrc = image;
                } else if (image && image.image_url) {
                  // API returns image objects with image_url property
                  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                  imgSrc = image.image_url.startsWith('http') 
                    ? image.image_url 
                    : `${baseUrl}${image.image_url.startsWith('/') ? '' : '/'}${image.image_url}`;
                } else if (image && image.url) {
                  imgSrc = image.url;
                }
                
                return (
                  <ImagePreview key={index}>
                    <img 
                      src={imgSrc} 
                      alt={`preview ${index}`}
                      onError={(e) => {
                        console.error('Image failed to load:', imgSrc);
                        e.target.style.display = 'none';
                      }}
                    />
                    {!readOnly && (
                      <RemoveImageButton 
                        onClick={() => {
                          // Only allow removal if there are more than 1 images
                          if (formData.images.length > 1) {
                            // Clean up blob URL when removing image
                            if (image instanceof File && imgSrc.startsWith('blob:')) {
                              URL.revokeObjectURL(imgSrc);
                            }
                            setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
                          } else {
                            setNotification({ show: true, message: 'Minimal 1 foto harus tersedia.', type: 'error' });
                          }
                        }}
                        disabled={formData.images.length <= 1}
                        title={formData.images.length <= 1 ? 'Minimal 1 foto harus tersedia' : 'Hapus foto'}
                      >
                        ×
                      </RemoveImageButton>
                    )}
                  </ImagePreview>
                );
              })}
            </ImagePreviewContainer>
            {/* Image validation feedback */}
            {!imageValidation.isPristine && (
              imageValidation.hasValidImages ? (
                <ImageValidationSuccess>
                  {formData.images.length} foto telah diupload
                </ImageValidationSuccess>
              ) : (
                <ImageValidationError>
                  {imageValidation.errorMessage}
                </ImageValidationError>
              )
            )}
          </FormGroup>
          
          <FormActions>
            <CancelButton type="button" onClick={onClose}>
              {readOnly ? 'Tutup' : 'Batal'}
            </CancelButton>
            {!readOnly && (
              <SubmitButton type="submit" disabled={loading || readOnly || !imageValidation.hasValidImages}>
                {loading ? (isEdit ? 'Menyimpan...' : 'Mengirim...') : (readOnly ? 'Tutup' : (isEdit ? 'Simpan Perubahan' : 'Kirim Laporan'))}
              </SubmitButton>
            )}
          </FormActions>
        </form>
      </FormContent>
    </FormWrapper>
  );
};

const FormWrapper = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  overflow: hidden;
  transition: background-color 0.3s;
`;

const FormHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border);
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  
  ${props => {
    switch(props.status) {
      case 'approved':
        return `
          background-color: #e6f8f5;
          color: #00a389;
        `;
      case 'pending':
        return `
          background-color: #fff3e0;
          color: #ff9800;
        `;
      case 'rejected':
        return `
          background-color: #feeeee;
          color: #dc3545;
        `;
      default:
        return '';
    }
  }}
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: var(--text-light);
  
  &:hover {
    background-color: var(--background-light);
  }
`;

const FormContent = styled.div`
  padding: 2rem;
  max-height: 70vh;
  overflow-y: auto;
  
  // Custom scrollbar for webkit browsers
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => (theme === 'dark' ? '#2c3e50' : '#ecf0f1')};
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => (theme === 'dark' ? '#4a627a' : '#bdc3c7')};
    border-radius: 4px;
    border: 2px solid transparent;
    background-clip: content-box;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => (theme === 'dark' ? '#5a7896' : '#a5aab0')};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--text-dark);
  }
  
  input, select, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-family: 'Poppins', sans-serif;
    font-size: 1rem;
    color: var(--text-dark);
    background-color: ${props => props.theme === 'dark' ? '#383838' : '#f5f5f5'};
    
    &:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 2px rgba(43, 93, 161, 0.2);
    }
  }
  
  textarea {
    resize: vertical;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  ${FormGroup} {
    flex: 1;
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const SubmitButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: var(--primary-light);
  }
`;

const CancelButton = styled.button`
  background-color: transparent;
  color: var(--text-dark);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--background-light);
  }
`;

const ImagePreviewContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const ImagePreview = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;

  ${ImagePreview}:hover & {
    opacity: 1;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

// Notification Toast styles (copied from InterupsiForm.js for consistency)
const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  min-width: 300px;
  background-color: ${props => props.$type === 'success' ? '#2ecc71' : '#e74c3c'};
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 9999;
  animation: slideIn 0.3s ease-out forwards;
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
`;
const NotificationMessage = styled.div`
  flex: 1;
  font-weight: 500;
`;
const CloseNotificationButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  margin-left: 10px;
  opacity: 0.8;
  transition: opacity 0.2s;
  &:hover { opacity: 1; }
`;

const TeacherSuggestContainer = styled.div`
  position: relative;
`;

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: var(--background);
  border: 1px solid var(--border);
  border-top: none;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
`;

const SuggestionItem = styled.div`
  padding: 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid var(--border);
  transition: background-color 0.2s;
  background-color: ${props => props.selected ? 'var(--primary-light)' : 'transparent'};
  
  &:hover {
    background-color: var(--background-light);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TeacherName = styled.div`
  font-weight: 500;
  color: var(--text-dark);
`;

const ImageValidationError = styled.div`
  background-color: #feeeee;
  color: #dc3545;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  margin-top: 0.5rem;
  font-size: 0.85rem;
  border-left: 3px solid #dc3545;
  display: flex;
  align-items: center;
  &::before {
    content: "⚠️";
    margin-right: 0.5rem;
  }
`;

const ImageValidationSuccess = styled.div`
  background-color: #e6f8f5;
  color: #00a389;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  margin-top: 0.5rem;
  font-size: 0.85rem;
  border-left: 3px solid #00a389;
  display: flex;
  align-items: center;
  &::before {
    content: "✓";
    margin-right: 0.5rem;
    font-weight: bold;
  }
`;

export default KompensasiForm;